import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Page banner',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a strong interior or lifestyle photo for this page banner — kitchens, living rooms, or finished apartment spaces. Prefer polished, well-lit shots. Skip logos, floor-plan diagrams, parking lots, and utility areas.',
    context:
      'Full-bleed background behind the page title on an inner-page banner, matching the home Hero image treatment.',
  },
};

export function HeroSolidBG({ tokenPrefix, title = 'Page' }: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const bgPath = `${prefix}.bg_image`;

  const bgImage = useSlotGroup(bgPath, bgImageDef);

  return (
    <section className="relative w-full min-h-[42vh] md:min-h-[48vh] max-h-[520px] overflow-hidden flex items-center">
      {/* Full-bleed cover — same treatment as home Hero › bg_image */}
      <EditableSlotGroup groupPath={bgPath} definition={bgImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || 'Page banner'}
              priority
              sizes="100vw"
              className="h-full w-full object-cover object-left-top"
            />
          ) : (
            <div className="h-full w-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Flat dark overlay — matches home Hero */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ backgroundColor: 'rgba(40, 40, 40, 0.3)' }}
        aria-hidden="true"
      />

      {/* Translucent content panel flush left — home Hero proportions,
          slightly narrower + thicker for inner-page titles. */}
      <div className="relative z-10 w-full pl-0 pr-4 sm:pr-6 md:pr-10 lg:pr-12 py-14 md:py-20 lg:py-24 pointer-events-none">
        <div className="hsb-content-panel hsb-entrance hsb-entrance-1 w-[21rem] max-w-[90vw] sm:w-[28rem] md:w-[32rem] pointer-events-auto">
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="hsb-entrance hsb-entrance-2 hsb-banner-title text-3xl sm:text-4xl md:text-5xl font-normal leading-[1.15] tracking-[0.04em] text-white"
          />
        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        @keyframes hsbEntrance {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hsb-entrance {
          animation: hsbEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hsb-entrance-1 { animation-delay: 0.05s; }
        .hsb-entrance-2 { animation-delay: 0.2s; }
        .hsb-content-panel {
          background: rgba(33, 34, 34, 0.64);
          border-top: 4px solid var(--vibe-color-secondary);
          padding: 2.5rem 1.5rem 2.75rem 1.75rem;
          margin-left: 0;
        }
        @media (min-width: 768px) {
          .hsb-content-panel {
            padding: 3rem 1.75rem 3.25rem 2.5rem;
          }
        }
        .hsb-banner-title {
          font-family: Georgia, 'Times New Roman', serif;
        }
        .hsb-banner-title p {
          margin: 0;
        }
        @media (prefers-reduced-motion: reduce) {
          .hsb-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Page Banner',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': bgImageDef,
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
